import {
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material'
import { navigate, routes, useParams } from '@cedarjs/router'
import { Metadata } from '@cedarjs/web'
import { useQuery } from '@cedarjs/web'
import { gql } from '@apollo/client'
import { useBreadcrumbs } from 'src/layouts/components/BreadcrumbsContext'
import RootPageStyledDiv from 'src/layouts/components/RootPageStyledDiv'
import EditIcon from '@mui/icons-material/Edit'
import SettingsIcon from '@mui/icons-material/Settings'
import ListIcon from '@mui/icons-material/List'
import PublishIcon from '@mui/icons-material/Publish'
import CopyIcon from '@mui/icons-material/CopyAll'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import LaunchIcon from '@mui/icons-material/Launch'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import useTabbedNavigation from 'src/hooks/useTabbedNavigation'
import { useState } from 'react'
import EditFormTab from './EditFormTab'
import SettingsTab from './SettingsTab'
import SubmissionsTab from './SubmissionsTab'
import TitleSettings from './TitleSettings'
import toast from 'react-hot-toast'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import usePreventNavigationForUnsavedChanges from 'src/hooks/usePreventNavigationForUnsavedChanges'
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal'
import PrintIcon from '@mui/icons-material/Print'
import { openModal } from 'src/slices/modal'
import { useDispatch } from 'react-redux'
import { useMutation } from '@cedarjs/web'
import { UPDATE_FORM_TEMPLATE_STATUS_MUTATION } from 'src/pages/FormsLandingPage/queries'
import printForm from 'src/utils/printForm'
import { CREATE_FORM_SUBMISSION_MUTATION } from 'src/pages/SubmitFormPage/queries'

const FORM_TEMPLATE_DETAILED_QUERY = gql`
  query FormTemplateDetailed($id: String!) {
    formTemplate(id: $id) {
      id
      title
      description
      status
      visibility
      formSchema
      createdAt
      updatedAt
      submissionCount
      createdBy {
        firstName
        lastName
      }
    }
  }
`

const CREATE_FILE_MUTATION = gql`
  mutation CreateFormSubmissionFile($input: CreateFileInput!) {
    createFile(input: $input) {
      id
      filename
      signedUploadUrl
    }
  }
`

type FormTemplatePageProps = {
  orgName: string
}

const tabValues = ['edit', 'settings', 'submissions']

const tabValueToIndex = tabValues.reduce((acc, value, index) => {
  acc[value] = index
  return acc
}, {})

const getStatusColor = (status: string) => {
  switch (status) {
    case 'PUBLISHED':
      return 'success'
    case 'DRAFT':
      return 'default'
    case 'ARCHIVED':
      return 'warning'
    default:
      return 'default'
  }
}

const FormTemplatePage = ({ orgName }: FormTemplatePageProps) => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const [tab, handleTabSwitching] = useTabbedNavigation('edit')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [linkMenuAnchor, setLinkMenuAnchor] = useState<null | HTMLElement>(null)

  const { confirmNavigation, denyNavigation } =
    usePreventNavigationForUnsavedChanges({
      hasUnsavedChanges: hasUnsavedChanges,
    })

  const { data, loading, error, refetch } = useQuery(
    FORM_TEMPLATE_DETAILED_QUERY,
    {
      variables: { id: id || '' },
      skip: !id,
    }
  )

  const [createFile] = useMutation(CREATE_FILE_MUTATION)
  const [createFormSubmission] = useMutation(CREATE_FORM_SUBMISSION_MUTATION)
  const [updateFormTemplateStatus] = useMutation(
    UPDATE_FORM_TEMPLATE_STATUS_MUTATION
  )

  const formTemplate = data?.formTemplate

  useBreadcrumbs([
    { icon: 'forms' },
    { label: 'Forms', to: routes.forms({ orgName }) },
    { label: formTemplate?.title || 'Form Template' },
  ])

  const handleUpdate = () => {
    refetch()
  }

  const createTabLabel = (
    label: string,
    hasUnsavedChanges: boolean
  ): React.ReactNode => {
    if (hasUnsavedChanges) {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {label}
          <FiberManualRecordIcon sx={{ fontSize: 8, color: 'warning.main' }} />
        </span>
      )
    }
    return label
  }

  const handleLinkMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setLinkMenuAnchor(event.currentTarget)
  }

  const handleLinkMenuClose = () => {
    setLinkMenuAnchor(null)
  }

  const handleSubmit = () => {
    const link = routes.submitForm({ id: formTemplate.id })
    window.open(link, '_blank')
    handleLinkMenuClose()
  }

  const handleCopyLink = () => {
    const link = `${window.location.origin}${routes.submitForm({
      id: formTemplate.id,
    })}`
    navigator.clipboard.writeText(link)
    toast.success('Link copied to clipboard')
    handleLinkMenuClose()
  }

  const handleUploadPdfSubmission = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.pdf,application/pdf'
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) return

      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file')
        return
      }

      try {
        toast.loading('Uploading PDF submission...', { id: 'pdf-upload' })

        // Step 1: Create file record
        const { data: createFileData } = await createFile({
          variables: {
            input: {
              originalFilename: file.name,
              title: file.name,
              description: 'PDF Form Submission',
            },
          },
        })

        const { id: fileId, signedUploadUrl } = createFileData.createFile

        // Step 2: Upload file to S3
        const uploadResponse = await fetch(signedUploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        })

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file')
        }

        // Step 3: Create form submission with file reference
        const { data: createFormSubmissionData } = await createFormSubmission({
          variables: {
            input: {
              formTemplateId: formTemplate.id,
              submissionData: {},
              submissionType: 'PDF',
              submissionFileId: fileId,
            },
          },
        })

        toast.success('PDF submission uploaded successfully!', {
          id: 'pdf-upload',
        })
        navigate(
          routes.viewFormSubmission({
            id: createFormSubmissionData.createFormSubmission.id,
            orgName,
          })
        )
      } catch (error) {
        console.error('Upload failed:', error)
        toast.error('Failed to upload PDF submission', { id: 'pdf-upload' })
      }
    }
    input.click()
    handleLinkMenuClose()
  }

  const handlePrintEmptyForm = () => {
    // Check if we have unsaved changes on the edit tab
    if (hasUnsavedChanges && tab === 'edit') {
      dispatch(
        openModal({
          name: 'confirmationModal',
          data: { discriminator: 'print-with-unsaved-changes' },
        })
      )
    } else {
      printForm(formTemplate)
    }
  }

  const handlePublish = async () => {
    try {
      await updateFormTemplateStatus({
        variables: {
          id: formTemplate.id,
          input: { status: 'PUBLISHED' },
        },
      })
      toast.success(`Form template published successfully`)
    } catch (error) {
      console.error('Error updating form template status:', error)
      toast.error('Failed to publish form template')
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-2">
        <Metadata title="Form Template" description="Form Template Details" />
        <div className="flex items-center justify-center py-8">
          <CircularProgress />
        </div>
      </div>
    )
  }

  if (error || !formTemplate) {
    return (
      <RootPageStyledDiv>
        <Metadata title="Form Template" description="Form Template Details" />
        <Alert severity="error">
          Form template not found or you don&apos;t have permission to view it.
        </Alert>
      </RootPageStyledDiv>
    )
  }

  return (
    <RootPageStyledDiv>
      <Metadata
        title={formTemplate.title}
        description={`Form Template: ${formTemplate.title}`}
      />

      {/* Header */}
      <div className="mb-3 mt-2">
        <div className="mb-1 flex items-start justify-between">
          <div className="mr-2 flex">
            <TitleSettings
              formTemplate={formTemplate}
              onUpdate={handleUpdate}
            />
            <div className="ml-2 mt-1 flex flex-shrink flex-wrap items-center gap-1">
              <Chip
                label={formTemplate.status}
                color={getStatusColor(formTemplate.status)}
                size="small"
              />
              <Chip
                label={formTemplate.visibility}
                variant="outlined"
                size="small"
              />
            </div>
          </div>

          <div className="flex gap-1">
            {formTemplate.status === 'PUBLISHED' ? (
              <Button
                variant="outlined"
                onClick={handleLinkMenuClick}
                endIcon={<ArrowDropDownIcon />}
              >
                Submit Response
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handlePublish}
                startIcon={<PublishIcon />}
              >
                Publish
              </Button>
            )}
            <Menu
              anchorEl={linkMenuAnchor}
              open={Boolean(linkMenuAnchor)}
              onClose={handleLinkMenuClose}
              MenuListProps={{
                'aria-labelledby': 'actions-button',
              }}
            >
              <MenuItem onClick={handleSubmit}>
                <LaunchIcon sx={{ mr: 1 }} />
                Fill out manually
              </MenuItem>
              <MenuItem onClick={handleCopyLink}>
                <CopyIcon sx={{ mr: 1 }} />
                Copy link to share
              </MenuItem>
              <MenuItem onClick={handleUploadPdfSubmission}>
                <UploadFileIcon sx={{ mr: 1 }} />
                Upload PDF submission
              </MenuItem>
            </Menu>
            <Button
              variant="outlined"
              onClick={handlePrintEmptyForm}
              startIcon={<PrintIcon />}
              disabled={!formTemplate.formSchema}
            >
              Print Empty Form
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-divider border-b">
          <Tabs
            value={tabValueToIndex[tab]}
            onChange={(_, newValue) =>
              handleTabSwitching(null, tabValues[newValue])
            }
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<EditIcon />}
              label={createTabLabel(
                'Edit',
                hasUnsavedChanges && tab === 'edit'
              )}
              iconPosition="start"
            />
            <Tab
              icon={<SettingsIcon />}
              label={createTabLabel(
                'Settings',
                hasUnsavedChanges && tab === 'settings'
              )}
              iconPosition="start"
            />
            <Tab
              icon={<ListIcon />}
              label={`Submissions (${formTemplate.submissionCount})`}
              iconPosition="start"
            />
          </Tabs>
        </div>
      </div>

      {/* Tab Content */}
      {tab === 'edit' && (
        <EditFormTab
          formTemplate={formTemplate}
          onUpdate={handleUpdate}
          onUnsavedChanges={setHasUnsavedChanges}
        />
      )}
      {tab === 'settings' && (
        <SettingsTab
          formTemplate={formTemplate}
          onUpdate={handleUpdate}
          onUnsavedChanges={setHasUnsavedChanges}
        />
      )}
      {tab === 'submissions' && <SubmissionsTab formTemplate={formTemplate} />}
      {/* Unsaved changes warning modal */}
      <ConfirmationModal
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost."
        confirmText="Leave Page"
        denyText="Stay"
        discriminator="unsaved-changes-warning"
        onConfirm={() => {
          confirmNavigation()
          setHasUnsavedChanges(false)
        }}
        onDeny={denyNavigation}
        confirmButtonProps={{
          color: 'warning',
        }}
      />

      {/* Print with unsaved changes confirmation modal */}
      <ConfirmationModal
        title="Unsaved Changes"
        message="You have unsaved changes to the form. The printed form will not include your recent changes. Do you want to print anyway?"
        confirmText="Print Anyway"
        denyText="Cancel"
        discriminator="print-with-unsaved-changes"
        onConfirm={() => {
          printForm(formTemplate)
        }}
        confirmButtonProps={{
          color: 'warning',
        }}
      />
    </RootPageStyledDiv>
  )
}

export default FormTemplatePage
